class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onResize = this.updateViewportBounds.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.updateViewportBounds();
    this.notification.classList.add('gb-cart-notification--viewport', 'animate', 'active');
    requestAnimationFrame(() => this.updateViewportBounds());
    window.addEventListener('resize', this.onResize, { passive: true });

    if (window.GBSmartAddonUpdate) {
      setTimeout(() => {
        window.GBSmartAddonUpdate();
        this.updateViewportBounds();
      }, 50);
      setTimeout(() => this.updateViewportBounds(), 250);
    }

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active', 'gb-cart-notification--viewport');
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onResize);
    this.notification.style.removeProperty('--gb-cart-notification-top');

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.decoratePlatePreview(parsedState);
    if (this.header) this.header.reveal();
    this.open();
  }

  updateViewportBounds() {
    const viewport = window.visualViewport;
    const viewportTop = viewport ? viewport.offsetTop : 0;
    const viewportHeight = viewport ? viewport.height : (window.innerHeight || document.documentElement.clientHeight || 0);
    const viewportBottom = viewportTop + viewportHeight;
    const gap = 12;

    let top = viewportTop + gap;
    if (this.header) {
      const headerRect = this.header.getBoundingClientRect();
      if (headerRect.bottom > viewportTop && headerRect.bottom < viewportBottom - 220) {
        top = Math.max(top, Math.ceil(headerRect.bottom));
      }
    }

    // Es müssen immer mindestens 260 px für die Notification übrig bleiben.
    top = Math.min(top, viewportBottom - 260 - gap);
    this.notification.style.setProperty('--gb-cart-notification-top', `${Math.max(viewportTop + gap, top)}px`);
  }

  decoratePlatePreview(parsedState) {
    const host = document.getElementById('cart-notification-product');
    if (!host || !parsedState) return;

    const handle = String(parsedState.handle || parsedState.product_handle || '').toLowerCase();
    let kind = '';
    if (handle === 'feinstaubplakette' || handle === 'environmental-badge-for-foreign-vehicles-registrations-for-german-green-zones') kind = 'green';
    else if (handle === 'blaue-e-plakette-fur-elektrofahrzeuge-zugelassen-ausserhalb-deutschlands') kind = 'blue';
    else if (handle === 'umweltplakette-gelabelt-mit-ihrem-kennzeichen-feinstaubplakette') kind = 'label';
    else if (handle === 'umweltplakette-euro-kennzeichen-schrift-deutschland') kind = 'euro';
    else if (handle === 'umweltplakette-feinstaubplakette-schriftart-wahlbar') kind = 'custom-font';
    if (!kind) return;

    const props = parsedState.properties || {};
    let plate = '';
    let fontName = '';
    for (const [key, value] of Object.entries(props)) {
      const k = String(key || '').toLowerCase();
      const v = String(value || '').trim();
      if (!v || v.includes('/uploads/')) continue;
      if (!plate && /kenteken|kennzeichen|licen[cs]e plate|immatriculation|targa|rejestr|spz|nummerplade/.test(k)) plate = v;
      if (/schriftart|font|lettertype/.test(k)) fontName = v;
    }

    const img = host.querySelector('img');
    if (!img || !img.parentElement) return;
    const media = img.parentElement;
    media.classList.add('gb-cart-preview-media');
    media.querySelectorAll('.gb-cart-preview').forEach((n) => n.remove());

    const overlay = document.createElement('div');
    overlay.className = `gb-cart-preview gb-cart-preview--${kind}`;
    overlay.setAttribute('aria-hidden', 'true');

    if (kind === 'custom-font') {
      const normalized = String(fontName || '').toLowerCase().trim();
      const assetMap = window.GBCartFontAssets || {};
      const asset = assetMap[normalized] || '';
      if (asset) {
        const sample = document.createElement('img');
        sample.className = 'gb-cart-preview__font-image';
        sample.src = asset;
        sample.alt = '';
        overlay.appendChild(sample);
      } else if (plate) {
        const span = document.createElement('span');
        span.className = 'gb-cart-preview__text';
        span.textContent = plate;
        overlay.appendChild(span);
      } else {
        return;
      }
    } else {
      if (!plate) return;
      const span = document.createElement('span');
      span.className = 'gb-cart-preview__text';
      span.textContent = plate;
      overlay.appendChild(span);
    }

    media.appendChild(overlay);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
